import React, { useCallback, useEffect, useRef, useState } from 'react';
import './FloatingHorizontalScrollbar.scss';

export interface FloatingHorizontalScrollbarProps {
    targetRef: React.RefObject<HTMLElement>;
    className?: string;
}

const SCROLL_SYNC_TOLERANCE_PX = 0.5;
const OVERFLOW_TOLERANCE_PX = 1;

export const FloatingHorizontalScrollbar = ({ targetRef, className }: FloatingHorizontalScrollbarProps) => {
    const stripRef = useRef<HTMLDivElement>(null);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [dimensions, setDimensions] = useState({
        left: 0,
        width: 0,
        scrollWidth: 0,
        hasOverflow: false,
    });

    const updateDimensions = useCallback(() => {
        const target = targetRef.current;
        if (!target) return;
        const rect = target.getBoundingClientRect();
        setDimensions({
            left: rect.left,
            width: rect.width,
            scrollWidth: target.scrollWidth,
            hasOverflow: target.scrollWidth > target.clientWidth + OVERFLOW_TOLERANCE_PX,
        });
    }, [targetRef]);

    useEffect(() => {
        const target = targetRef.current;
        if (!target) return;

        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(target);
        if (target.firstElementChild) {
            resizeObserver.observe(target.firstElementChild);
        }

        const intersectionObserver = new IntersectionObserver(
            ([entry]) => setIsIntersecting(entry.isIntersecting),
            { threshold: 0 }
        );
        intersectionObserver.observe(target);

        const handleTargetScroll = () => {
            const strip = stripRef.current;
            if (!strip) return;
            if (Math.abs(strip.scrollLeft - target.scrollLeft) > SCROLL_SYNC_TOLERANCE_PX) {
                strip.scrollLeft = target.scrollLeft;
            }
        };

        target.addEventListener('scroll', handleTargetScroll, { passive: true });
        window.addEventListener('resize', updateDimensions);
        window.addEventListener('scroll', updateDimensions, { passive: true });

        updateDimensions();

        return () => {
            resizeObserver.disconnect();
            intersectionObserver.disconnect();
            target.removeEventListener('scroll', handleTargetScroll);
            window.removeEventListener('resize', updateDimensions);
            window.removeEventListener('scroll', updateDimensions);
        };
    }, [targetRef, updateDimensions]);

    const isVisible = isIntersecting && dimensions.hasOverflow;

    // The strip mounts only while visible, so align it with the target on each appearance.
    useEffect(() => {
        if (isVisible && stripRef.current && targetRef.current) {
            stripRef.current.scrollLeft = targetRef.current.scrollLeft;
        }
    }, [isVisible, targetRef]);

    const handleStripScroll = () => {
        const strip = stripRef.current;
        const target = targetRef.current;
        if (!strip || !target) return;
        if (Math.abs(target.scrollLeft - strip.scrollLeft) > SCROLL_SYNC_TOLERANCE_PX) {
            target.scrollLeft = strip.scrollLeft;
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div
            ref={stripRef}
            className={`floating-horizontal-scrollbar${className ? ` ${className}` : ''}`}
            onScroll={handleStripScroll}
            style={{
                left: `${dimensions.left}px`,
                width: `${dimensions.width}px`,
            }}
        >
            <div
                className="floating-horizontal-scrollbar__spacer"
                style={{ width: `${dimensions.scrollWidth}px` }}
            />
        </div>
    );
};
